"use client";

import Link from "next/link";
import Image from "next/image";

type BrandMarkProps = {
  variant?: "header" | "footer";
};

export default function BrandMark({ variant = "header" }: BrandMarkProps) {
  const isHeader = variant === "header";
  // Single source of scale: the brand text size lives on the Link, and the
  // logo is em-sized so it always matches the text — at every viewport.
  const brandTextSize = isHeader ? 'clamp(1.3rem, 4vw, 1.75rem)' : '1.75rem';

  return (
    <Link 
      href="/" 
      className={`brand-mark brand-mark-${variant}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        // No flex gap: icon-96.png already carries transparent padding on its
        // sides, which supplies the optical gap. An extra gap doubles up and
        // reads as a hole between logo and wordmark.
        gap: 0,
        textDecoration: 'none',
        color: 'var(--text-primary)',
        fontSize: brandTextSize
      }}
    >
      <Image 
        src="/icon-192.png" 
        alt="FinanceLives Logo" 
        width={192}
        height={192}
        priority
        loading="eager"
        quality={90}
        style={{
          // Measured: the visible tile spans only ~58% of icon-192.png's canvas
          // (rest is transparent padding). A 1.7em box renders the visible
          // mark at ~1em — the same optical height as the brand text — and the
          // negative margins trim the transparent side padding (~0.37em per
          // side at this scale) so the mark sits flush with the wordmark.
          width: '1.7em',
          height: '1.7em',
          marginLeft: '-0.35em',
          marginRight: '-0.25em',
          objectFit: 'contain',
          borderRadius: 'var(--radius-sm)'
        }}
      />
      <span 
        className="slanted-brand"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '1em',
          fontWeight: 'var(--fw-extrabold)',
          lineHeight: '1',
          letterSpacing: '0.01em',
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap',
          fontStyle: 'italic'
        }}
      >
        FinanceLives
      </span>
    </Link>
  );
}
